import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Heading,
  Text,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Progress,
  useColorModeValue,
  Icon,
  HStack,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  SimpleGrid,
  Divider,
  useToast
} from '@chakra-ui/react';
import {
  MdGavel,
  MdAssignment,
  MdCheckCircle,
  MdCancel,
  MdPending,
  MdDescription,
  MdTrendingUp,
  MdAccessTime
} from 'react-icons/md';
import { FaBalanceScale, FaFileContract } from 'react-icons/fa';
import api from '../../../../services/api';

const JuridicoDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [availableStats, setAvailableStats] = useState(null);
  const [error, setError] = useState(null);
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [statsResponse, availableResponse] = await Promise.all([
        api.get('/juridico/stats'),
        api.get('/juridico/available/stats')
      ]);

      setStats(statsResponse.data.stats);
      setAvailableStats(availableResponse.data.stats);
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Erro ao carregar dados do dashboard');
      toast({
        title: 'Erro ao carregar dados',
        description: 'Não foi possível carregar as informações do dashboard',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      pendente: 'gray',
      em_analise: 'blue',
      aprovado: 'green',
      reprovado: 'red',
      revisao_necessaria: 'orange'
    };
    return colors[status] || 'gray';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pendente: MdPending,
      em_analise: MdAccessTime,
      aprovado: MdCheckCircle,
      reprovado: MdCancel,
      revisao_necessaria: MdDescription
    };
    return icons[status] || MdDescription;
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  const completionRate = stats?.total > 0 
    ? Math.round((stats.completed / stats.total) * 100)
    : 0;

  // Calculate total available precatorios
  const totalAvailable = availableStats 
    ? Object.values(availableStats).reduce((acc, state) => acc + state.available, 0)
    : 0;

  const totalAvailableValue = availableStats
    ? Object.values(availableStats).reduce((acc, state) => acc + state.valorTotal, 0)
    : 0;

  return (
    <Box>
      <Heading size="lg" mb={6} color={textColor}>
        Dashboard Jurídico
      </Heading>

      {/* Key Metrics */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6} mb={8}>
        <GridItem>
          <Card bg={cardBg} shadow="sm">
            <CardBody>
              <Stat>
                <Flex justify="space-between" align="flex-start">
                  <Box>
                    <StatLabel color={mutedColor}>Disponíveis para Análise</StatLabel>
                    <StatNumber fontSize="2xl">{totalAvailable}</StatNumber>
                    <StatHelpText>
                      {formatCurrency(totalAvailableValue)}
                    </StatHelpText>
                  </Box>
                  <Icon as={MdGavel} w={8} h={8} color="purple.500" />
                </Flex>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg={cardBg} shadow="sm">
            <CardBody>
              <Stat>
                <Flex justify="space-between" align="flex-start">
                  <Box>
                    <StatLabel color={mutedColor}>Em Análise</StatLabel>
                    <StatNumber fontSize="2xl">{stats?.inProgress || 0}</StatNumber>
                    <StatHelpText>
                      {completionRate}% concluído
                    </StatHelpText>
                  </Box>
                  <Icon as={MdAccessTime} w={8} h={8} color="blue.500" />
                </Flex>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg={cardBg} shadow="sm">
            <CardBody>
              <Stat>
                <Flex justify="space-between" align="flex-start">
                  <Box>
                    <StatLabel color={mutedColor}>Aprovados</StatLabel>
                    <StatNumber fontSize="2xl" color="green.500">
                      {stats?.byStatus?.aprovado?.count || 0}
                    </StatNumber>
                    <StatHelpText>
                      {formatCurrency(stats?.byStatus?.aprovado?.valorTotal || 0)}
                    </StatHelpText>
                  </Box>
                  <Icon as={MdCheckCircle} w={8} h={8} color="green.500" />
                </Flex>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg={cardBg} shadow="sm">
            <CardBody>
              <Stat>
                <Flex justify="space-between" align="flex-start">
                  <Box>
                    <StatLabel color={mutedColor}>Reprovados</StatLabel>
                    <StatNumber fontSize="2xl" color="red.500">
                      {stats?.byStatus?.reprovado?.count || 0}
                    </StatNumber>
                    <StatHelpText>
                      {formatCurrency(stats?.byStatus?.reprovado?.valorTotal || 0)}
                    </StatHelpText>
                  </Box>
                  <Icon as={MdCancel} w={8} h={8} color="red.500" />
                </Flex>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
        {/* Available by State */}
        <GridItem>
          <Card bg={cardBg} shadow="sm">
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="md" color={textColor}>
                  Precatórios Disponíveis por Estado
                </Heading>
                <Icon as={FaFileContract} w={5} h={5} color="purple.500" />
              </Flex>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {availableStats && Object.entries(availableStats).map(([state, data]) => (
                  <Box key={state}>
                    <Flex justify="space-between" align="center" mb={2}>
                      <HStack>
                        <Badge colorScheme="purple">{state}</Badge>
                        <Text fontSize="sm" color={textColor}>
                          {data.available} precatórios
                        </Text>
                      </HStack>
                      <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                        {formatCurrency(data.valorTotal)}
                      </Text>
                    </Flex>
                    <Progress 
                      value={totalAvailable > 0 ? (data.available / totalAvailable) * 100 : 0} 
                      size="xs" 
                      colorScheme="purple" 
                      borderRadius="full"
                    />
                  </Box>
                ))}
                {(!availableStats || Object.keys(availableStats).length === 0) && (
                  <Text fontSize="sm" color={mutedColor} textAlign="center">
                    Nenhum precatório disponível para análise
                  </Text>
                )}
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Analysis by State */}
        <GridItem>
          <Card bg={cardBg} shadow="sm">
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="md" color={textColor}>
                  Análises por Estado
                </Heading>
                <Icon as={FaBalanceScale} w={5} h={5} color="blue.500" />
              </Flex>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {stats?.byState && stats.byState.map((stateData) => (
                  <Box key={stateData._id}>
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontWeight="semibold" color={textColor}>
                        {stateData._id}
                      </Text>
                      <Text fontSize="sm" color={mutedColor}>
                        Total: {stateData.total}
                      </Text>
                    </Flex>
                    <SimpleGrid columns={4} spacing={2}>
                      <Box textAlign="center">
                        <Text fontSize="xs" color="green.500">Aprovados</Text>
                        <Text fontWeight="bold">{stateData.aprovados}</Text>
                      </Box>
                      <Box textAlign="center">
                        <Text fontSize="xs" color="red.500">Reprovados</Text>
                        <Text fontWeight="bold">{stateData.reprovados}</Text>
                      </Box>
                      <Box textAlign="center">
                        <Text fontSize="xs" color="blue.500">Em Análise</Text>
                        <Text fontWeight="bold">{stateData.emAnalise}</Text>
                      </Box>
                      <Box textAlign="center">
                        <Text fontSize="xs" color="gray.500">Pendentes</Text>
                        <Text fontWeight="bold">{stateData.pendentes}</Text>
                      </Box>
                    </SimpleGrid>
                    <Divider mt={2} />
                  </Box>
                ))}
                {(!stats?.byState || stats.byState.length === 0) && (
                  <Text fontSize="sm" color={mutedColor} textAlign="center">
                    Nenhuma análise realizada ainda
                  </Text>
                )}
              </VStack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Progress Overview */}
      <Card bg={cardBg} shadow="sm" mt={6}>
        <CardHeader>
          <Heading size="md" color={textColor}>
            Progresso Geral
          </Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Box>
              <Flex justify="space-between" mb={2}>
                <Text color={mutedColor}>Taxa de Conclusão</Text>
                <Text fontWeight="semibold" color={textColor}>{completionRate}%</Text>
              </Flex>
              <Progress value={completionRate} size="sm" colorScheme="blue" borderRadius="full" />
            </Box>
            
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mt={4}>
              <Box textAlign="center">
                <Icon as={MdAssignment} w={6} h={6} color="purple.500" mb={2} />
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  {stats?.total || 0}
                </Text>
                <Text fontSize="sm" color={mutedColor}>Total Designados</Text>
              </Box>
              <Box textAlign="center">
                <Icon as={MdCheckCircle} w={6} h={6} color="green.500" mb={2} />
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {stats?.completed || 0}
                </Text>
                <Text fontSize="sm" color={mutedColor}>Concluídos</Text>
              </Box>
              <Box textAlign="center">
                <Icon as={MdAccessTime} w={6} h={6} color="blue.500" mb={2} />
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {stats?.inProgress || 0}
                </Text>
                <Text fontSize="sm" color={mutedColor}>Em Progresso</Text>
              </Box>
              <Box textAlign="center">
                <Icon as={MdTrendingUp} w={6} h={6} color="orange.500" mb={2} />
                <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                  {stats?.byStatus?.revisao_necessaria?.count || 0}
                </Text>
                <Text fontSize="sm" color={mutedColor}>Revisão</Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default JuridicoDashboard;